import { FC, useEffect, useRef, useState } from 'react'
import { DialogActions, DialogContent, DialogTitle } from '@mui/material'
import TinyMCEEditor from '../../../components/tinymce'
import { ContainedButton, OutlinedButton } from '../styled'
import './style.scss'
import { editDocumentation } from '../../../services/document.service'
import { useSelector } from 'react-redux'
import { RootState } from '../../../store/store'
import { Editor } from 'tinymce'

interface DocInTinyMceProps {
  handleClosePopup: () => void
}

const DocInTinyMce: FC<DocInTinyMceProps> = ({ handleClosePopup }) => {
  const documentState = useSelector((state: RootState) => state.document)
  const [documentationContentInHtml, setDocumentationContentInHtml] = useState<string>("")
  const editorRef = useRef<Editor | null>(null);
  const jsonToHTML = (data: any): string => {
    if (typeof data === 'string') {
      // Convert newlines to <br/>
      data = data.replace(/\n/g, '<br/>');

      // Handle bold text (surrounded by **)
      data = data.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');

      // Handle code blocks (triple backticks)
      data = data.replace(/```(.*?)```/g, '<pre class="language-markup"><code>$1</code></pre>');

      // Handle headings (for example, **Retrieving Public UUID:** becomes <h2>)
      if (/^\*\*/.test(data)) {
        data = `<h2>${data.replace(/\*\*(.*?)\*\*/g, '$1')}</h2>`;
      }

      return `<p>${data}</p>`;
    }

    if (Array.isArray(data)) {
      return `<ul>${data.map(item => `<li>${jsonToHTML(item)}</li>`).join('')}</ul>`;
    }

    if (typeof data === 'object' && data !== null) {
      return Object.entries(data)
        .map(([key, value]) => {
          return `<div><strong>${key}:</strong> ${jsonToHTML(value)}</div>`;
        })
        .join('');
    }

    return '';
  };

  const htmlToJSON = (html: string): any => {
    // Create a DOM parser
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    const parseElement = (element: HTMLElement | null): any => {
      if (!element) return null;

      if (element.tagName === 'P') {
        let content = element.innerHTML;

        // Handle bold text (convert <strong> to **text**)
        content = content.replace(/<strong>(.*?)<\/strong>/g, '**$1**');

        // Handle code blocks (convert <pre><code> to ```code```)
        content = content.replace(
          /<pre class="language-markup"><code>(.*?)<\/code><\/pre>/g,
          '```$1```'
        );

        // Handle <br/> tags (convert to newlines)
        content = content.replace(/<br\s*\/?>/g, '\n');

        // Handle headings (convert <h2> to **text**)
        if (element.previousElementSibling?.tagName === 'H2') {
          const heading = `**${element.previousElementSibling.textContent}**`;
          return `${heading}\n${content}`;
        }

        return content;
      }

      if (element.tagName === 'UL') {
        // Handle unordered lists (<ul> -> Array)
        return Array.from(element.children).map((child) =>
          parseElement(child as HTMLElement)
        );
      }

      if (element.tagName === 'LI') {
        return element.textContent?.trim() || null;
      }

      if (element.tagName === 'DIV') {
        // Handle objects (<div> -> key-value pairs)
        const children = Array.from(element.children);
        return children.reduce((obj: any, child) => {
          if (child.tagName === 'STRONG') {
            const key = child.textContent?.replace(':', '').trim() || '';
            const value = parseElement(child.nextElementSibling as HTMLElement);
            obj[key] = value;
          }
          return obj;
        }, {});
      }

      return element.textContent?.trim() || null;
    };

    // Start parsing the document body
    return parseElement(doc.body.firstElementChild as HTMLElement);
  };

  const handleSaveDocumentation = async () => {
    //future useage
    // await editDocumentation({
    //   "article_id": documentState?.documentationContent?.article_id,
    //   "name": documentState?.documentationContent?.name,
    //   "text": documentState?.documentationContent?.text,
    //   "issue_id": 96269
    // })
    //handleClosePopup() 
    if (!documentationContentInHtml) {
      console.log("htmlToJSON", htmlToJSON(documentationContentInHtml))
    }
  }

  useEffect(() => {
    if (documentState.documentationContent) {
      setDocumentationContentInHtml(jsonToHTML(documentState.documentationContent))
    }
  }, [documentState.documentationContent])

  return (
    <div className='DocInTinyMce-container'>
      <DialogTitle>
        <div className='document-header'>Documentation for your Code</div>
        <div className='document-description'>feel free to edit and save the document</div>
      </DialogTitle>
      <DialogContent className='fileUpload-body'>
        <TinyMCEEditor handleOnChange={(e: any) => setDocumentationContentInHtml(e)} editorValue={documentationContentInHtml} editorRef={editorRef} />
      </DialogContent>
      <DialogActions>
        <div className='dialog-action-container'>
          <OutlinedButton onClick={handleClosePopup}>
            Cancel
          </OutlinedButton>
          <ContainedButton disabled={!documentationContentInHtml} onClick={handleSaveDocumentation}>
            Save
          </ContainedButton>
        </div>
      </DialogActions>
    </div>
  )
}

export default DocInTinyMce